import type { JSONContent } from '@tiptap/core';
import type { Fragment } from '@tiptap/pm/model';
import { flow } from 'fp-ts/function';

import type { PmConverters } from './../pm-converters/collect';
import type { PmJson } from './../pm-converters/types';
import { isRichContentEmpty } from './../ricos-content';
import { RichContent } from './../ricos-schema/legacy/wix/rich_content/v1';
import { getEmptyContent } from './../editor-common';

import type { RicosEditor } from './ricos-editor';

export class RicosSchema {
  private schema: RichContentSchema;

  private initialContent: JSONContent;

  private initialTiptapContent: Fragment | undefined;

  private editor?: RicosEditor;

  constructor(initialContent: RichContent | undefined, converters: PmConverters) {
    this.initialContent = converters.toPm(
      initialContent?.nodes?.length ? initialContent : getEmptyContent()
    );
    this.schema = new RichContentSchema(converters);
  }

  setEditor(editor: RicosEditor): void {
    this.initialTiptapContent = editor.adapter.tiptapEditor.state.doc.content;
    this.editor = editor;
    this.schema.setEditor(editor);
  }

  setContent(content: RichContent): void {
    this.schema.setContent(content);
  }

  getContent = (): RichContent => {
    return this.schema.getContent();
  };

  getTiptapContent(): JSONContent {
    if (!this.editor) {
      return this.initialContent;
    }
    return this.editor.adapter.tiptapEditor.getJSON();
  }

  getID(): string {
    return this.schema.getID();
  }

  isContentChanged(): boolean {
    const editorContent = this.editor?.adapter.tiptapEditor.state.doc.content;
    return !!editorContent && !this.initialTiptapContent?.eq(editorContent);
  }

  isEmpty(): boolean {
    return this.schema.isEmpty();
  }
}

export class RichContentSchema {
  private editor!: RicosEditor;

  private converters!: PmConverters;

  constructor(converters: PmConverters) {
    this.converters = converters;
  }

  setEditor(editor: RicosEditor) {
    this.editor = editor;
  }

  setContent(content: RichContent): void {
    const jsonContent = this.converters.toPm(content);
    this.editor.adapter.setTiptapContent(jsonContent);
  }

  getContent(): RichContent {
    const tiptapContent = this.editor?.adapter.tiptapEditor.getJSON();
    return normalizeRichContent(this.converters.fromPm(tiptapContent as PmJson)) as RichContent;
  }

  getID(): string {
    return this.getContent().metadata?.id as string;
  }

  isEmpty(): boolean {
    const content = this.getContent();
    return isRichContentEmpty(content);
  }
}

const normalizeRichContent = flow(RichContent.fromJSON, RichContent.toJSON);
